import * as React from 'react';
import styles from './index.less';
import * as AHooks from 'ahooks';
import FComponentsLib from '@freelog/components-lib';
import { FUtil, FServiceAPI, FI18n } from '@freelog/tools-lib';
import { Space } from 'antd';
import { DisplayItem } from '../Editor';

export interface PolicyTemplateInfo {
  id: string;
  title: string;
  code: string;
  translation: string;
  displayData: DisplayItem[];
}

interface PolicyTemplatesProps {
  resourceTypeCode: string[];
  nodeID?: number;
  onSelect?(policyInfo: PolicyTemplateInfo): void;
}

function PolicyTemplates({ resourceTypeCode, nodeID, onSelect }: PolicyTemplatesProps) {
  const [$templates, set$templates, get$templates] = FUtil.Hook.useGetState<PolicyTemplateInfo[]>(
    [],
  );
  AHooks.useMount(async () => {
    const result = await getPolicyTemplateInfos({ resourceTypeCode, nodeID });
    // console.log(result, 'result sedfi8ojkew;lkfjslkdj')
    set$templates(result);
  });

  return (
    <Space size={20} direction={'vertical'} style={{ width: '100%' }}>
      {$templates.map((t, i) => {
        return (
          <PolicyTemplate
            key={t.id}
            title={t.title}
            text={t.code}
            translation={t.translation}
            onSelect={() => {
              // console.log(t, 't98ioskdjfksdjlfsjdflksjdlkj');
              onSelect?.(t);
            }}
          />
        );
      })}
    </Space>
  );
}

export default PolicyTemplates;

interface PolicyTemplateProps {
  title: string;
  translation: string;
  text: string;
  showSelectBtn?: boolean;

  onSelect?(): void;
}

export function PolicyTemplate({
  text,
  title,
  translation,
  onSelect,
  showSelectBtn = true,
}: PolicyTemplateProps) {
  const [visible, setVisible] = React.useState<'none' | 'code' | 'view'>('none');

  return (
    <div className={styles.PolicyTemplate}>
      <div className={styles.header}>
        <FComponentsLib.FTitleText type='h1' text={title} />
        {showSelectBtn && (
          <FComponentsLib.FRectBtn
            type='primary'
            size='small'
            onClick={() => {
              onSelect && onSelect();
            }}
          >
            {
              // 选择
              FI18n.i18nNext.tAuto('createauthplan_btn_select')
            }
          </FComponentsLib.FRectBtn>
        )}
      </div>
      <div style={{ height: 15 }} />
      <div className={styles.translation}>
        <FComponentsLib.FCodeFormatter code={translation} />
      </div>
      <div style={{ height: 15 }} />
      <div className={styles.navs}>
        {visible === 'code' ? (
          <FComponentsLib.FTextBtn
            type='primary'
            onClick={() => {
              setVisible('none');
            }}
          >
            {
              // 隐藏代码
              FI18n.i18nNext.tAuto('createauthplan_btn_hidecode')
            }
          </FComponentsLib.FTextBtn>
        ) : (
          <FComponentsLib.FTextBtn
            type='primary'
            onClick={() => {
              setVisible('code');
            }}
          >
            {
              // 显示代码
              FI18n.i18nNext.tAuto('createauthplan_btn_showcode')
            }
          </FComponentsLib.FTextBtn>
        )}

        <div style={{ width: 20 }} />
      </div>

      {visible === 'code' && (
        <>
          <div style={{ height: 15 }} />
          <FComponentsLib.FCodeFormatter code={text} />
        </>
      )}

      {visible === 'view' && (
        <>
          <div style={{ height: 15 }} />
          <div style={{ height: 200 }} />
        </>
      )}
    </div>
  );
}

/**
 * 将包含 ${变量名} 的字符串解析为数组（使用 split）
 * @param str 输入的字符串
 * @returns 解析后的字符串数组
 */
function parseStringWithVariables(str: string): string[] {
  // 使用 split 分割，正则中的捕获组会保留分隔符
  const parts = str.split(/(\$\{[^}]+\})/);

  // 过滤掉空字符串
  return parts.filter((part) => part.length > 0);
}

export async function getPolicyTemplateInfos({
  resourceTypeCode,
  nodeID,
}: {
  resourceTypeCode: string[];
  nodeID?: number;
}): Promise<PolicyTemplateInfo[]> {
  // console.log(resourceTypeCode, nodeID, 'resourceTypeCode, nodeID wioejfsd;jkf5346贴如果发的jk;jlk');
  const {
    data,
  }: {
    data: {
      _id: string;
      title: string;
      template: string;
      reportTranslate: string;
      report: string;
      // compileType: 'normal' | 'collection'  以后用来区分普通资源和合集，暂未使用
      reportUiTemplate: {
        id: string;
        type: string;
        uiSectionDefaultValue: string | number;
        uiSectionType: 'number' | 'select';
        selectOptions: {
          label: string;
          value: string;
        }[];
      }[];
    }[];
  } = await FServiceAPI.Policy.policyTemplates({
    resourceTypeCodes4Resource: !nodeID ? resourceTypeCode : undefined,
    resourceTypeCodes4Presentable: nodeID ? resourceTypeCode : undefined,
  });

  return data.map((d, i: number) => {
    const coreText: string = d.report;
    const parts = parseStringWithVariables(coreText);
    const displayData = parts.map((part) => {
      if (!part.startsWith('${')) {
        return {
          id: part,
          type: 'text',
          text: {
            value: part,
          },
        };
      }
      const id: string = part.slice(2, -1);
      const currentUiTemplate = (d.reportUiTemplate || []).find((item) => item.id === id);

      const type: 'number' | 'select' | 'datetime' =
        currentUiTemplate?.uiSectionType === 'number'
          ? 'number'
          : currentUiTemplate?.uiSectionType === 'select'
            ? 'select'
            : 'datetime';
      if (type === 'number') {
        // 相对时间：旧 id 含 .RelativeTimeEvent，以后改成 Time.Relative
        const isRelativeTimeEvent =
          currentUiTemplate?.id.includes('.RelativeTimeEvent') ||
          currentUiTemplate?.id.includes('Time.Relative');
        // 支付：旧 id 含 .TransactionEvent，以后改成 Transaction.Pay；Transaction.PayForRank 也有可能要用到
        const isTransactionEvent =
          currentUiTemplate?.id.includes('.TransactionEvent') ||
          currentUiTemplate?.id.includes('Transaction.Pay') ||
          currentUiTemplate?.id.includes('Transaction.PayForRank');

        let min = 0.01;
        let precision = 2;
        if (isRelativeTimeEvent) {
          min = 1;
          precision = 0;
        }
        return {
          id: id,
          type: 'number',
          number: {
            value: currentUiTemplate?.uiSectionDefaultValue as number,
            min: min,
            precision: precision,
          },
        };
      }
      if (type === 'select') {
        return {
          id: id,
          type: 'select',
          select: {
            value: currentUiTemplate?.uiSectionDefaultValue as string,
            options: currentUiTemplate?.selectOptions as {
              label: string;
              value: string;
            }[],
          },
        };
      }
      if (type === 'datetime') {
        return {
          id: id,
          type: 'datetime',
          datetime: {
            value: currentUiTemplate?.uiSectionDefaultValue as string,
          },
        };
      }
    }) as DisplayItem[];
    return {
      id: d._id,
      title: d.title,
      code: d.template,
      translation: d.reportTranslate,
      displayData: displayData,
      // displayTranslation: '',
    };
  });
}
